import { useMemo } from 'react';

import { Box, Flex, Text } from '@chakra-ui/react';
import { HelpLink } from 'components/HelpLink';
import { Currency } from 'components/Number';
import PropTypes from 'prop-types';

import { forecastStartMonth, formatMonthLabel } from './cashflowFeeExposureHelpers';
import { monthRange } from './companyCash';
import { customerStage } from './pipelineExposure';
import { REPAYMENT_VECTOR_SOURCES, repaymentVectorModel } from './repaymentVectors';

// Where a marketing number came from. "saved" is real marketing data; every
// other source is generated by the calculation and gets a coloured cell.
export const MARKETING_SOURCES = {
  saved: { label: 'Marketing data', background: '#FFFFFF', color: undefined },
  grown: { label: 'Grown from the last saved month', background: '#FFF4E5', color: '#9C4221' },
  default: { label: 'Default marketing', background: '#E6F0FF', color: '#1A4E9C' },
  none: { label: 'No marketing, so not funded', background: '#FDECEC', color: '#C53030' },
};

// marketingUsedModel builds one row per customer and one column per marketing month.
export function marketingUsedModel(customers, pipelineClients) {
  const monthSet = new Set();
  const rows = [];
  const missing = [];
  (customers || []).forEach((customer) => {
    const marketing = customer?.marketing || [];
    if (!marketing.length) {
      missing.push(customer);
      return;
    }
    const byMonth = {};
    marketing.forEach((item) => {
      if (!item?.month) return;
      monthSet.add(item.month);
      byMonth[item.month] = item;
    });
    rows.push({
      customerId: customer.customerId,
      name: customer.companyName || customer.customerId,
      stage: customerStage(customer, pipelineClients),
      byMonth,
      marketingView: customer.marketingView || null,
    });
  });
  const months = [...monthSet].sort();
  const totals = months.map((month) => rows.reduce((sum, row) => sum + (Number(row.byMonth[month]?.spend) || 0), 0));
  const generated = months.map((month) => rows.reduce((sum, row) => {
    const item = row.byMonth[month];
    return item && item.source !== 'saved' ? sum + (Number(item.spend) || 0) : sum;
  }, 0));
  return { months, rows, totals, generated, missing };
}

function formatAmount(value) {
  return Math.round(Number(value) || 0).toLocaleString('en-US');
}

function formatPercent(fraction) {
  return `${Math.round((Number(fraction) || 0) * 1000) / 10}%`;
}

// marketingViewNote explains a cell of a deal that funds from a custom marketing
// view: the cell shows total marketing, the funding is sized from the view's part.
export function marketingViewNote(item, marketingView) {
  if (!marketingView || !item) return '';
  const window = marketingView.from && marketingView.through
    ? `, the average of ${formatMonthLabel(marketingView.from)} – ${formatMonthLabel(marketingView.through)}`
    : '';
  const parts = [
    `Funded on marketing view ${marketingView.viewId}: ${formatAmount(item.fundingSpend)}`
      + ` (${formatPercent(marketingView.ratio)} of total marketing${window}).`,
  ];
  const factor = viewRepaymentFactorForMonth(item, marketingView);
  if (factor !== 1) {
    parts.push(`The view also drives repayment, so repayments are x ${factor.toFixed(2)} (total / view).`);
  }
  return parts.join(' ');
}

// viewRepaymentFactorForMonth is the total / view factor one funding repays with:
// the month's own spend for a complete month, 1 / ratio for the current and later
// months. 1 when the view does not drive repayment.
export function viewRepaymentFactorForMonth(item, marketingView) {
  if (!marketingView?.forRepayment) return 1;
  const spend = Number(item?.spend) || 0;
  const funding = Number(item?.fundingSpend) || 0;
  if (spend > 0 && funding > 0) return spend / funding;
  const ratio = Number(marketingView.ratio) || 0;
  return ratio > 0 ? 1 / ratio : 1;
}

// cohortVectorNote explains a month whose funding repays from the cohort's own
// sales-cohort row (complete marketing months, as the asset tape does) instead of
// the customer's repayment vector.
export function cohortVectorNote(item, marketingView) {
  const roas = item?.cohortRoas;
  if (!Array.isArray(roas) || !roas.length) return '';
  const factor = viewRepaymentFactorForMonth(item, marketingView);
  let cumulative = 0;
  const steps = roas.slice(0, 4).map((value, index) => {
    cumulative += (Number(value) || 0) * factor;
    return `M${index + 1} ${formatPercent(cumulative)}`;
  });
  return `Repays from this cohort's own sales-cohort row, as the asset tape does, not the repayment vector: ${steps.join(', ')} of the funding, cumulative.`;
}

// fundingModel builds one row per customer with the funding (growth investment)
// in each calendar month of the window. Actual months come from installments,
// later months from the projection.
export function fundingModel(customers, pipelineClients, startMonth, endMonth) {
  const window = monthRange(startMonth, endMonth);
  const fromData = new Set();
  (customers || []).forEach((customer) => {
    (customer?.months || []).forEach((row) => {
      if (row?.month && Number(row.growthInvestments) > 0) fromData.add(row.month);
    });
  });
  const months = window.length ? window : [...fromData].sort();
  const rows = (customers || [])
    .map((customer) => {
      const byMonth = {};
      (customer?.months || []).forEach((row) => {
        if (row?.month) byMonth[row.month] = row;
      });
      return {
        customerId: customer.customerId,
        name: customer.companyName || customer.customerId,
        stage: customerStage(customer, pipelineClients),
        byMonth,
        total: months.reduce((sum, month) => sum + (Number(byMonth[month]?.growthInvestments) || 0), 0),
      };
    })
    .filter((row) => row.total > 0);
  const totals = months.map((month) => rows.reduce(
    (sum, row) => sum + (Number(row.byMonth[month]?.growthInvestments) || 0),
    0
  ));
  return { months, rows, totals, grandTotal: totals.reduce((sum, value) => sum + value, 0) };
}

function FundingTable({ customers, pipelineClients, startMonth, endMonth }) {
  const model = useMemo(
    () => fundingModel(customers, pipelineClients, startMonth, endMonth),
    [customers, pipelineClients, startMonth, endMonth]
  );
  const forecastMonth = forecastStartMonth();
  return (
    <Box mt={10}>
      <Text fontSize="sm" fontWeight={700} letterSpacing="0.06em" mb={1}>FUNDING</Text>
      <Text fontSize="sm" color="gray.600" mb={3}>
        The funding (growth investment) each customer receives in each calendar month, From to Through on Customers.
        Green months are actual funding from installments. Blue months are projected from the marketing above.
      </Text>
      {!model.rows.length ? (
        <Text color="gray.500">No funding in these months.</Text>
      ) : (
        <Box overflowX="auto">
          <Box as="table" w="max-content" minW="100%" fontFamily="body" fontSize="sm">
            <thead>
              <tr>
                <th style={stickyHeader}>Customer</th>
                <th style={{ ...monthHeader, textAlign: 'left' }}>Stage</th>
                {model.months.map((month) => (
                  <th
                    key={month}
                    style={{ ...monthHeader, background: month < forecastMonth ? '#70AD47' : '#2E75B6' }}
                  >
                    {formatMonthLabel(month)}
                  </th>
                ))}
                <th style={monthHeader}>Total</th>
              </tr>
            </thead>
            <tbody>
              {model.rows.map((row) => (
                <tr key={row.customerId}>
                  <td style={{ ...stickyCell, fontWeight: 600 }}>{row.name}</td>
                  <td style={{ ...cell, textAlign: 'left', whiteSpace: 'nowrap' }}>{row.stage}</td>
                  {model.months.map((month) => {
                    const item = row.byMonth[month];
                    const amount = Number(item?.growthInvestments) || 0;
                    return (
                      <td
                        key={month}
                        data-projected={item ? String(!!item.isProjected) : undefined}
                        title={item?.bindingConstraint ? `Limited by ${item.bindingConstraint}` : undefined}
                        style={cell}
                      >
                        {amount ? <Currency value={amount} maximumFractionDigits={0} /> : ''}
                      </td>
                    );
                  })}
                  <td style={{ ...cell, fontWeight: 700 }}><Currency value={row.total} maximumFractionDigits={0} /></td>
                </tr>
              ))}
              <tr>
                <td style={{ ...stickyCell, ...totalCell }}>Total</td>
                <td style={totalCell} />
                {model.totals.map((total, index) => (
                  <td key={model.months[index]} style={{ ...cell, ...totalCell }}>
                    {total ? <Currency value={total} maximumFractionDigits={0} /> : ''}
                  </td>
                ))}
                <td style={{ ...cell, ...totalCell }}><Currency value={model.grandTotal} maximumFractionDigits={0} /></td>
              </tr>
            </tbody>
          </Box>
        </Box>
      )}
    </Box>
  );
}

FundingTable.propTypes = {
  customers: PropTypes.array,
  pipelineClients: PropTypes.array,
  startMonth: PropTypes.string,
  endMonth: PropTypes.string,
};

function RepaymentVectorTable({ customers, pipelineClients, settings }) {
  const model = useMemo(
    () => repaymentVectorModel(customers, pipelineClients, settings),
    [customers, pipelineClients, settings]
  );
  const columns = Array.from({ length: model.months }, (_, index) => index + 1);
  return (
    <Box mt={10}>
      <Flex align="center" gap={3} mb={1}>
        <Text fontSize="sm" fontWeight={700} letterSpacing="0.06em">REPAYMENT VECTORS</Text>
        <HelpLink sectionId="sources-uses-repayment-vector" variant="subtle" fontSize="sm">How it is calculated</HelpLink>
      </Flex>
      <Text fontSize="sm" color="gray.600" mb={3}>
        The share of each projected funding repaid by the end of each repayment month, cumulative (month 1 is the first
        month money comes back). Non-live and future customers use their own vector, or the generic curve when Generic
        is checked; the curve multiplier is not applied by the calculation. Live customers use the vector from their
        sales cohorts times the deal&apos;s revenue margin, and, when the deal&apos;s marketing view drives repayment,
        times total / view marketing, as the asset tape does. A live funding of a complete marketing month (marked †
        under Marketing) repays from that cohort&apos;s own row instead. Grey cells are past the end of the curve: its last monthly
        step repeats there, the same as the calculation.
      </Text>
      {!model.rows.length ? (
        <Text color="gray.500">No repayment vectors to show.</Text>
      ) : (
        <Box overflowX="auto">
          <Box as="table" w="max-content" minW="100%" fontFamily="body" fontSize="sm">
            <thead>
              <tr>
                <th style={stickyHeader}>Customer</th>
                <th style={{ ...monthHeader, textAlign: 'left' }}>Stage</th>
                <th style={{ ...monthHeader, textAlign: 'left' }}>Vector</th>
                {columns.map((month) => (
                  <th key={month} style={monthHeader}>{`M${month}`}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {model.rows.map((row) => (
                <tr key={row.customerId}>
                  <td style={{ ...stickyCell, fontWeight: 600 }}>{row.name}</td>
                  <td style={{ ...cell, textAlign: 'left', whiteSpace: 'nowrap' }}>{row.stage}</td>
                  <td
                    style={{
                      ...cell,
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      color: row.source === 'fallback' ? '#C53030' : undefined,
                    }}
                    title={[
                      row.margin ? `Revenue margin ${row.margin}` : '',
                      row.viewFactor && row.viewFactor !== 1
                        ? `Includes x ${row.viewFactor.toFixed(2)} for the marketing view (total / view spend)`
                        : '',
                    ].filter(Boolean).join('. ') || undefined}
                  >
                    {REPAYMENT_VECTOR_SOURCES[row.source]}
                  </td>
                  {row.cumulative.map((value, index) => {
                    const repeated = index >= row.curveLength;
                    return (
                      <td
                        key={columns[index]}
                        data-repeated={repeated ? 'true' : undefined}
                        style={{ ...cell, color: repeated ? '#A0AEC0' : undefined }}
                      >
                        {`${value}%`}
                      </td>
                    );
                  })}
                  {!row.cumulative.length && <td style={{ ...cell, color: '#C53030' }} colSpan={columns.length}>No vector</td>}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
      {model.liveWithout.length > 0 && (
        <Text fontSize="xs" color="gray.500" mt={3}>
          {`No repayment vector saved for ${model.liveWithout.join(', ')}. `}
          Live vectors are saved by Save &amp; Run with the updated deal-service.
        </Text>
      )}
    </Box>
  );
}

RepaymentVectorTable.propTypes = {
  customers: PropTypes.array,
  pipelineClients: PropTypes.array,
  settings: PropTypes.object,
};

function Legend() {
  return (
    <Flex gap={4} wrap="wrap" mb={3} fontSize="xs">
      {Object.entries(MARKETING_SOURCES).map(([key, source]) => (
        <Flex key={key} align="center" gap={2}>
          <Box w="14px" h="14px" borderRadius="3px" borderWidth="1px" borderColor="gray.300" bg={source.background} />
          <Text>{source.label}</Text>
        </Flex>
      ))}
    </Flex>
  );
}

function MarketingUsedTable({ customers, pipelineClients, settings, startMonth, endMonth }) {
  const model = useMemo(() => marketingUsedModel(customers, pipelineClients), [customers, pipelineClients]);

  return (
    <Box>
      <Text fontSize="sm" fontWeight={700} letterSpacing="0.06em" mb={1}>MARKETING</Text>
      <Text fontSize="sm" color="gray.600" mb={3}>
        The marketing each customer&apos;s projected fundings are sized from, by marketing month. Live customers are
        funded the month after (July marketing sizes the August funding). Non-live and future customers are funded Lag
        months after. White cells come from marketing data. Coloured cells were generated by the calculation, as
        explained under How marketing is filled in on Customers.
      </Text>
      <Text fontSize="sm" color="gray.600" mb={3}>
        * The deal funds from a custom marketing view. The cell shows total marketing; the funding is sized from the
        view&apos;s spend: its actual spend for a complete month, otherwise its share of total marketing over the last
        6 complete months. Hover for the amount.
      </Text>
      <Text fontSize="sm" color="gray.600" mb={3}>
        † A live customer&apos;s complete marketing month repays from that cohort&apos;s own sales-cohort row (actual
        revenue, then its prediction), as the asset tape does, instead of the repayment vector. Hover for the curve.
      </Text>
      <Legend />
      {!model.rows.length ? (
        <Text color="gray.500">No projected fundings use marketing yet. Press Save &amp; Run on Customers.</Text>
      ) : (
        <Box overflowX="auto">
          <Box as="table" w="max-content" minW="100%" fontFamily="body" fontSize="sm">
            <thead>
              <tr>
                <th style={stickyHeader}>Customer</th>
                <th style={{ ...monthHeader, textAlign: 'left' }}>Stage</th>
                {model.months.map((month) => (
                  <th key={month} style={monthHeader}>{formatMonthLabel(month)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {model.rows.map((row) => (
                <tr key={row.customerId}>
                  <td style={{ ...stickyCell, fontWeight: 600 }}>{row.name}</td>
                  <td style={{ ...cell, textAlign: 'left', whiteSpace: 'nowrap' }}>{row.stage}</td>
                  {model.months.map((month) => {
                    const item = row.byMonth[month];
                    const source = item ? MARKETING_SOURCES[item.source] || MARKETING_SOURCES.saved : null;
                    const viewNote = marketingViewNote(item, row.marketingView);
                    const cohortNote = cohortVectorNote(item, row.marketingView);
                    const title = [source?.label, viewNote, cohortNote].filter(Boolean).join('. ');
                    return (
                      <td
                        key={month}
                        title={title || undefined}
                        data-view={viewNote ? 'true' : undefined}
                        data-cohort-vector={cohortNote ? 'true' : undefined}
                        data-source={item?.source}
                        style={{
                          ...cell,
                          background: source?.background,
                          color: source?.color,
                        }}
                      >
                        {item && Number(item.spend) > 0 ? <Currency value={item.spend} maximumFractionDigits={0} /> : item ? '—' : ''}
                        {viewNote && Number(item.spend) > 0 ? '*' : ''}
                        {cohortNote && Number(item.spend) > 0 ? '†' : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr>
                <td style={{ ...stickyCell, ...totalCell }}>Total</td>
                <td style={totalCell} />
                {model.totals.map((total, index) => (
                  <td key={model.months[index]} style={{ ...cell, ...totalCell }}>
                    {total ? <Currency value={total} maximumFractionDigits={0} /> : ''}
                  </td>
                ))}
              </tr>
              <tr>
                <td style={{ ...stickyCell, color: '#9C4221' }}>of which generated</td>
                <td />
                {model.generated.map((total, index) => (
                  <td key={model.months[index]} style={{ ...cell, color: '#9C4221' }}>
                    {total ? <Currency value={total} maximumFractionDigits={0} /> : ''}
                  </td>
                ))}
              </tr>
            </tbody>
          </Box>
        </Box>
      )}
      {model.missing.length > 0 && (
        <Text fontSize="xs" color="gray.500" mt={3}>
          {`No marketing detail for ${model.missing.map((customer) => customer.companyName || customer.customerId).join(', ')}. `}
          These customers have no projected fundings left, or the forecast was saved before this screen existed. Press Save &amp; Run on Customers to refresh it.
        </Text>
      )}
      <FundingTable customers={customers} pipelineClients={pipelineClients} startMonth={startMonth} endMonth={endMonth} />
      <RepaymentVectorTable customers={customers} pipelineClients={pipelineClients} settings={settings} />
    </Box>
  );
}

const stickyHeader = {
  position: 'sticky',
  left: 0,
  background: '#fff',
  textAlign: 'left',
  padding: '8px 12px',
  zIndex: 1,
};

const monthHeader = {
  padding: '8px 12px',
  textAlign: 'right',
  color: '#fff',
  fontWeight: 700,
  background: '#2E75B6',
  whiteSpace: 'nowrap',
};

const cell = {
  padding: '8px 12px',
  textAlign: 'right',
  borderBottom: '1px solid #EDF2F7',
};

const stickyCell = {
  position: 'sticky',
  left: 0,
  padding: '8px 12px',
  zIndex: 1,
  minWidth: 200,
  background: '#fff',
  borderBottom: '1px solid #EDF2F7',
};

const totalCell = {
  fontWeight: 700,
  background: '#D6E4F0',
};

MarketingUsedTable.propTypes = {
  customers: PropTypes.array,
  pipelineClients: PropTypes.array,
  settings: PropTypes.object,
  startMonth: PropTypes.string,
  endMonth: PropTypes.string,
};

export default MarketingUsedTable;
